import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../../../../Clients/store';
import { getClientsShort } from '../../../../../Clients/store/selectors';
import {
  getCalendarWorkers,
  getCalendarWorkerServices,
  getTaskById,
} from '../../../../store/selectors';
import InputSelector from '../../../../../Common/CustomSelector/InputSelector';
import MultiplySelector from '../../../../../Common/CustomSelector/MultiplySelector';
import TextField from '../../../../../Common/TextField';
import { ReactComponent as Calendar } from '../../../../../../assets/svg/icons/calendar_blue.svg';
import { ReactComponent as Scissors } from '../../../../../../assets/svg/icons/scissors_blue.svg';
import { ReactComponent as Person } from '../../../../../../assets/svg/icons/person_blue.svg';
import { ReactComponent as Armchair } from '../../../../../../assets/svg/icons/armchair_blue.svg';
import { ReactComponent as Bin } from '../../../../../../assets/svg/icons/trash_red.svg';
import { ReactComponent as ClockIcon } from '../../../../../../assets/svg/icons/orari_blue.svg';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import styles from './TaskModal.module.scss';
import Switcher from '../../../../../Common/Switcher';
import HoveredInfoIcon from '../../../../../Common/HoveredInfoIcon';
import { Button } from '@mui/material';
import clx from 'classnames';
import CustomDatePicker from '../../../../../Common/CustomDatePicker';
import CustomTimePicker from '../../../../../Common/CustomTimePicker';
import { schedulerActions } from '../../../../store';
import ConfirmDeleteModal from '../../../../../Common/ConfirmDeleteModal';
import useWindowSize from '../../../../../../utils/useWindowSize';
import ConfirmSecondaryModal from '../../../../../Common/ConfirmSecondaryModal';
import constants from 'shared/Loader/store/constants';
import { getSpecifLoader } from 'shared/Loader/store/selectors';
import store from 'store/store';
import ConfirmModal from 'Containers/Common/ConfirmModal';
import { getActiveSalonId } from 'Containers/Auth/store/selectors';
import Spinner from 'Containers/Common/Spinner';
import { useTranslation } from 'react-i18next';
import RecurrenceFrequencySelect from '../RecurrenceFrequencySelect';

const composeBookingNote = (values) => {
  const note = String(values.description || '').trim();
  const walkIn = !values.client_id ? String(values.clientName || '').trim() : '';
  if (walkIn && note && !note.includes(walkIn)) return `${walkIn}\n${note}`;
  return note || walkIn;
};

const FALLBACK_RECURRENCE = {
  1: 'Ogni settimana',
  2: 'Ogni due settimane',
  3: 'Ogni tre settimane',
  4: 'Ogni quattro settimane',
};

const PartialTask = ({
  taskValue: { day, workerId, task },
  closeModal,
  isEditForm = false,
  isMobile,
  isTablet = false,
  clients,
}) => {
  const { t } = useTranslation();
  // Su tablet la nota resta un campo proprio: il nome scritto non deve passare dalla lista clienti.
  const showNote = Boolean(isTablet) || !isMobile;
  const dispatch = useDispatch();
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [confirmationBookingModal, setConfirmationBookingModal] = useState({});
  const salonId = useSelector(getActiveSalonId());
  const [isLoading, setIsLoading] = useState(false);

  const recurrenceOptions = [
    {
      name: t('scheduler.taskModal.recurrence.weekly', FALLBACK_RECURRENCE[1]),
      value: 1,
      days: 7,
    },
    {
      name: t('scheduler.taskModal.recurrence.biweekly', FALLBACK_RECURRENCE[2]),
      value: 2,
      days: 14,
    },
    {
      name: t('scheduler.taskModal.recurrence.triweekly', FALLBACK_RECURRENCE[3]),
      value: 3,
      days: 21,
    },
    {
      name: t('scheduler.taskModal.recurrence.quadriweekly', FALLBACK_RECURRENCE[4]),
      value: 4,
      days: 28,
    },
  ];

  const loading = useSelector(getSpecifLoader(constants.CLIENT));
  const clientsRequested = useRef(false);
  useEffect(() => {
    if ((clients || []).length > 0 || clientsRequested.current) return;
    clientsRequested.current = true;
    dispatch(
      clientActions.GET_CLIENTS.REQUEST({}, () => { }, {
        loader: constants.CLIENT,
      }),
    );
  }, [dispatch, clients]);

  const workers = useSelector(getCalendarWorkers());
  const taskToEdit = isEditForm ? useSelector(getTaskById(task.idprenotazione)) : {};

  const initialValues = {
    worker_id: workerId || '',
    client_id: isEditForm ? taskToEdit.utente : '',
    service_ids:
      isEditForm && taskToEdit?.servizi?.length
        ? taskToEdit.servizi.map((task) => task.idServizio)
        : [],
    date: task?.unixStartTime ? dayjs(task.unixStartTime * 1000) : dayjs(),
    startDate: task?.unixStartTime ? dayjs(task.unixStartTime * 1000) : dayjs(),
    endDate: task?.unixEndTime
      ? dayjs(task.unixEndTime * 1000)
      : (task?.unixStartTime ? dayjs(task.unixStartTime * 1000) : dayjs()).add(30, 'minutes'),
    recurring_booking: false,
    recurrence: 1,
    recurrenceEndDate: task?.unixStartTime
      ? dayjs(task.unixStartTime * 1000).add(1, 'month')
      : null,
    services: useSelector(getCalendarWorkerServices()),
    description: isEditForm ? taskToEdit.nota : '',
    clientName: '',
  };
  const validationSchema = isEditForm
    ? {
      worker_id: yup.string().required('Seleziona una poltrona'),
      startDate: yup.date().required('Data obbligatoria'),
      endDate: yup.date().required('Data obbligatoria'),
    }
    : {
      worker_id: yup.string().required('Seleziona una poltrona'),
      client_id: yup
        .number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue == null ? null : value,
        )
        .nullable()
        .when(['description', 'clientName'], ([description, clientName], schema) =>
          String(description || '').trim() || String(clientName || '').trim()
            ? schema
            : schema.required('Selezione il cliente o inserisci una nota con il suo nome'),
        ),
      service_ids: yup
        .array()
        .min(1, 'seleziona almeno un servizio')
        .required('Seleziona almeno un servizio'),
      startDate: yup.date().required('Data obbligatoria'),
      endDate: yup.date().required('Data obbligatoria'),
      description: yup.string(),
      clientName: yup.string(),
      recurring_booking: yup.boolean(),
      recurrence: yup
        .number()
        .when('recurring_booking', ([recurring_booking], schema) =>
          recurring_booking ? schema.required('Campo obbligatorio') : schema.nullable(true),
        ),
      recurrenceEndDate: yup
        .date()
        .when('recurring_booking', ([recurring_booking], schema) =>
          recurring_booking ? schema.required('Campo obbligatorio') : schema.nullable(true),
        ),
    };
  const removeBooking = (recurrence) => {
    dispatch(
      schedulerActions.DELETE_CALENDAR_TASK.REQUEST({
        deleteAllRicorrenti: recurrence,
        idPoltrona: taskToEdit.poltrona,
        idPrenotazione: taskToEdit.idprenotazione,
        ricorrente: taskToEdit.ricorrente,
      }),
    );
    closeModal();
  };
  const [sendNotifica, setSendNotifica] = useState(false);
  const [confirmNotificationModal, setConfirmNotificationModal] = useState({});
  const BoxNotice = ({ sendNotifica, setSendNotifica = () => { } }) => {
    return (
      <div className={styles.notice}>
        Invia una notifica al cliente &nbsp;
        <input
          className={styles.boxNotice}
          checked={sendNotifica}
          onChange={(e) => {
            e.preventDefault();
            setSendNotifica(e.target.checked);
          }}
          type='checkbox'
        />
      </div>
    );
  };

  const errCallback = (values) => {
    if (values && typeof values === 'object') {
      setConfirmationBookingModal(values);
    }
    setIsLoading(false);
  };

  const loadingAfterCall = () => {
    setIsLoading(false);
    closeModal();
  };


  const editOrCreateBooking = (values) => {
    if (isEditForm) {
      const formedTask = {
        idPrenotazione: taskToEdit.idprenotazione,
        orario_start: values.startDate.format('HH:mm') + ':00',
        orario_end: values.endDate.format('HH:mm') + ':00',
        data_prenotazione: values.startDate.format('YYYY-MM-DD'),
        poltrona: values.worker_id,
        nota: values.description,
        sendNotificaUpdateUser: sendNotifica ?? false,
      };
      // Chiude subito: la card si aggiorna in optimistic update nella saga.
      dispatch(
        schedulerActions.EDIT_CALENDAR_TASK.REQUEST(formedTask, null, {
          skipLoader: true,
          errCallback,
        }),
      );
      closeModal();
      return;
    }

    setIsLoading(true);
    const services = values.services
      .filter(
        (service) =>
          +service.worker_id === +values.worker_id && values.service_ids.includes(service.value),
      )
      .map((service) => ({ idservezio: service.value, nome: service.name }));
    const formedValues = {
      idPoltrona: values.worker_id,
      idUtente: values.client_id || null,
      servizi: services,
      oraInizio: values.startDate.format('HH:mm') + ':00',
      oraFine: values.endDate.format('HH:mm') + ':00',
      dataPrenotazione: (isMobile ? initialValues.date : values.startDate).format('YYYY-MM-DD'),
      nota: composeBookingNote(values),
      recurrence: {
        enabled: values.recurring_booking,
        to: values.recurring_booking && values.recurrenceEndDate.format('YYYY-MM-DD'),
        recurrenceType: values.recurrence,
      },
      saveAnyway: values.saveAnyway ?? false,
    };
    dispatch(
      schedulerActions.ADD_CALENDAR_TASK.REQUEST(
        formedValues,
        () => setTimeout(loadingAfterCall, 1000),
        { values, errCallback },
      ),
    );
  };
  return (
    <>
      {isLoading && <Spinner blocking />}
      <ConfirmSecondaryModal
        isModalOpen={confirmationModalIsOpen}
        setIsModalOpen={setConfirmationModalIsOpen}
        title={t('scheduler.taskModal.cancelBooking.title')}
        subtitle={
          task?.ricorrente
            ? t('scheduler.taskModal.cancelBooking.recurringSubtitle')
            : t('scheduler.taskModal.cancelBooking.confirmSubtitle')
        }
        buttons={
          task?.ricorrente
            ? [t('scheduler.taskModal.cancelBooking.cancelThis'), t('scheduler.taskModal.cancelBooking.cancelAll')]
            : [t('scheduler.taskModal.cancelBooking.cancel')]
        }
        buttonsActions={[() => removeBooking(false), () => removeBooking(true)]}
      />
      <ConfirmModal /** confirm when there are conflicts */
        isModalOpen={Object.values(confirmationBookingModal).length}
        setIsModalOpen={() => setConfirmationBookingModal({})}
        title={
          (isEditForm
            ? t('scheduler.taskModal.booking.edit_double_confirm')
            : `${t('scheduler.taskModal.booking.edit_double_confirm')}`) + " " +
            t('scheduler.taskModal.booking.content_double_confirm')
        }
        content={
          isEditForm && taskToEdit.utente != salonId ? (
            <BoxNotice sendNotifica={sendNotifica} setSendNotifica={setSendNotifica} />
          ) : null
        }
        action={() => editOrCreateBooking(confirmationBookingModal)}
      />
      <ConfirmModal /** confirm notification only */
        isModalOpen={Object.values(confirmNotificationModal).length}
        setIsModalOpen={() => setConfirmNotificationModal({})}
        title={t('scheduler.taskModal.booking.edit_double_confirm')}
        content={
          isEditForm && taskToEdit.utente != salonId ? (
            <BoxNotice sendNotifica={sendNotifica} setSendNotifica={setSendNotifica} />
          ) : null
        }
        action={() => editOrCreateBooking(confirmNotificationModal)}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape(validationSchema)}
        onSubmit={(values) => {
          const schedules = store.getState().schedulerReducer.calendar.appointments;
          const skippedDays = recurrenceOptions.find(
            (option) => option.value === values.recurrence,
          ).days;
          const possibleDays = [dayjs(values.startDate).format('YYYY-MM-DD')];
          const endDate = dayjs(values.recurrenceEndDate).unix();
          let day = dayjs(values.startDate).add(skippedDays, 'days');
          if (values.recurring_booking)
            while (day.unix() <= endDate) {
              possibleDays.push(day.format('YYYY-MM-DD'));
              day = day.add(skippedDays, 'days');
            }

          const hasConflite = Object.entries(schedules).some(
            ([day, schedule]) =>
              possibleDays.includes(day) &&
              schedule.some((x) => {
                if (
                  x.poltrona !== values.worker_id ||
                  x.idprenotazione === taskToEdit.idprenotazione
                )
                  return false;
                const start = dayjs(x.orario_start, 'HH:mm:ss');
                const end = dayjs(x.orario_end, 'HH:mm:ss');
                if (
                  start.unix() < dayjs(values.endDate.format(`HH:mm:ss`), 'HH:mm:ss').unix() &&
                  end.unix() > dayjs(values.startDate.format(`HH:mm:ss`), 'HH:mm:ss').unix()
                )
                  return true;
                return false;
              }),
          );
          if (hasConflite) {
            const value2 = {
              ...values,
              saveAnyway: true
            };
            setConfirmationBookingModal(value2);
          }
          else isEditForm ? setConfirmNotificationModal(values) : editOrCreateBooking(values);
        }}
      >
        {({ values, setFieldValue, errors, touched, setFieldTouched }) => {
          const servicesDuration = values.services
            .filter(
              (service) =>
                +service.worker_id === +values.worker_id &&
                values.service_ids.includes(service.value),
            )
            .reduce((acc, item) => acc + (item.duration || 0), 0);
          const durationMinutes = Math.max(
            5,
            values.endDate && values.startDate
              ? values.endDate.diff(values.startDate, 'minute')
              : 30,
          );

          const setStartKeepingDuration = (nextStart) => {
            const kept = servicesDuration || durationMinutes || 30;
            setFieldValue('startDate', nextStart);
            setFieldTouched('startDate', false);
            setFieldValue('endDate', nextStart.add(kept, 'minutes'));
            setFieldTouched('endDate', false);
          };

          const setBookingDate = (date) => {
            if (!date) return;
            const nextStart = values.startDate
              .year(date.year())
              .month(date.month())
              .date(date.date());
            setStartKeepingDuration(nextStart);
            setFieldValue('recurrenceEndDate', date.add(1, 'month'));
            setFieldTouched('recurrenceEndDate', false);
          };

          return (
          <Form
            className={clx(styles.create_task_form, {
              [styles.create_task_form_mobile]: isMobile,
            })}
          >
            <div className={styles.body} data-modal-scroll>
              <div className={styles.inputs_wrapper}>
                {!isMobile && (
                  <div className={styles.input_wrapper}>
                    <InputSelector
                      options={workers}
                      value={values.worker_id}
                      setValue={(id) => {
                        setFieldValue('worker_id', id);
                        setFieldTouched('worker_id', false);
                        if (id !== values.worker_id && !isEditForm) {
                          setFieldValue('service_ids', []);
                          setFieldTouched('service_ids', false);
                        }
                      }}
                      placeholder={t('scheduler.taskModal.selectChair')}
                      title={t('scheduler.taskModal.chair')}
                      icon={<Armchair />}
                      withIcon={true}
                      error={touched.worker_id && errors.worker_id}
                      isEmptyMessage={t('scheduler.taskModal.noChairs')}
                    />
                  </div>
                )}
                <div className={styles.input_wrapper}>
                  <MultiplySelector
                    readOnly={true}
                    closeOnClick={true}
                    options={values.services.filter(
                      (service) => +service.worker_id === +values.worker_id,
                    )}
                    value={values.service_ids}
                    setValue={(ids) => {
                      const activeServicesDuration = values.services
                        .filter(
                          (service) =>
                            +service.worker_id === +values.worker_id && ids.includes(service.value),
                        )
                        .reduce((acc, item) => acc + item.duration, 0);
                      const newEndTime = values.startDate.add(
                        activeServicesDuration || 30,
                        'minutes',
                      );
                      setFieldValue('service_ids', ids, false);
                      setFieldValue('endDate', newEndTime, false);
                    }}
                    placeholder={t('scheduler.taskModal.selectService')}
                    title={t('scheduler.taskModal.service')}
                    icon={<Scissors />}
                    withIcon={true}
                    disabled={isEditForm}
                    error={touched.service_ids && errors.service_ids}
                    isEmptyMessage={t('scheduler.taskModal.noServices')}
                  />
                </div>
              </div>
              <div className={styles.time_and_client}>
                <div className={styles.when_section}>
                  {!isMobile && (
                    <CustomDatePicker
                      value={values.startDate}
                      setValue={setBookingDate}
                      placeholder={t('scheduler.taskModal.selectDate')}
                      title={t('scheduler.taskModal.date')}
                      icon={<Calendar />}
                      withIcon={true}
                      error={touched.startDate && errors.startDate}
                    />
                  )}
                  <div className={styles.time_row}>
                    <CustomTimePicker
                      value={values.startDate}
                      setValue={setStartKeepingDuration}
                      placeholder={t('scheduler.taskModal.startTime')}
                      title={t('scheduler.taskModal.startTime')}
                      icon={<ClockIcon />}
                      withIcon={true}
                      timeStep={5}
                      error={touched.startDate && errors.startDate}
                    />
                    <CustomTimePicker
                      value={values.endDate}
                      setValue={(date) => {
                        setFieldValue('endDate', date);
                        setFieldTouched('endDate', false);
                      }}
                      placeholder={t('scheduler.taskModal.endTime')}
                      title={t('scheduler.taskModal.endTime')}
                      icon={<ClockIcon />}
                      withIcon={true}
                      timeStep={5}
                      minTime={values.startDate.add(5, 'minutes')}
                      error={touched.endDate && errors.endDate}
                    />
                  </div>
                  <p className={styles.time_step_hint}>
                    {t('scheduler.taskModal.timeStepHint')}
                  </p>
                </div>

                <div className={styles.inputs_wrapper}>
                  <div className={styles.input_wrapper}>
                    <InputSelector
                      loading={loading}
                      options={clients}
                      newValue={showNote ? values.clientName : values.description}
                      acceptNewValue={isMobile}
                      clearNewValueOnSelect={!showNote}
                      openOnFocus={!isTablet}
                      value={values.client_id}
                      setNewValue={(value) => {
                        setFieldValue(showNote ? 'clientName' : 'description', value);
                        setFieldTouched('client_id', false);
                      }}
                      setValue={(id) => {
                        setFieldValue('client_id', id);
                        setFieldTouched('client_id', false);
                      }}
                      placeholder={t('scheduler.taskModal.selectClient')}
                      title={showNote ? t('scheduler.taskModal.client') : t('scheduler.taskModal.clientOrNote')}
                      icon={<Person />}
                      withIcon={true}
                      disabled={isEditForm}
                      error={touched.client_id && errors.client_id}
                      isEmptyMessage={t('scheduler.taskModal.noClients')}
                      number={true}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.field_block}>
                {showNote && (
                  <Field
                    name='description'
                    id='description'
                    label={t('scheduler.taskModal.note')}
                    placeholder={t('scheduler.taskModal.notePlaceholder')}
                    component={TextField}
                    type='text'
                    textarea={true}
                    height={'88px'}
                    width={'100%'}
                    margin={'0'}
                  />
                )}
                {!isEditForm && (
                  <div className={styles.recurrence_panel}>
                    <div className={styles.switcher_wrapper}>
                      <HoveredInfoIcon
                        iconPosition='right'
                        description={t('scheduler.taskModal.recurringInfo')}
                        title={t('scheduler.taskModal.recurringBooking')}
                      />
                      <div className={styles.switcher_container}>
                        <Switcher
                          value={!values.recurring_booking}
                          setValue={(bool) => setFieldValue('recurring_booking', bool)}
                          isFullWidth={false}
                        />
                      </div>
                    </div>

                    {values.recurring_booking ? (
                      <div className={styles.recurrence_fields}>
                        <div className={styles.time_input_wrapper}>
                          <CustomDatePicker
                            value={values.recurrenceEndDate}
                            setValue={(date) => {
                              setFieldValue('recurrenceEndDate', date);
                              setFieldTouched('recurrenceEndDate', false);
                            }}
                            placeholder={t('scheduler.taskModal.selectDate')}
                            title={t('scheduler.taskModal.until')}
                            icon={<Calendar />}
                            withIcon={true}
                            minDate={values.startDate}
                            maxDate={dayjs(values.startDate).add(1, 'year').toDate()}
                            error={touched.recurrenceEndDate && errors.recurrenceEndDate}
                          />
                        </div>
                        <RecurrenceFrequencySelect
                          label={t('scheduler.taskModal.recurrence.label')}
                          value={values.recurrence ?? 1}
                          options={recurrenceOptions}
                          onChange={(next) => {
                            setFieldValue('recurrence', next);
                            setFieldTouched('recurrence', false);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            <div
              className={clx(styles.form_actions, {
                [styles.edit_actions]: isEditForm,
              })}
            >
              <Button
                onClick={() => setConfirmationModalIsOpen(true)}
                className={styles.delete_button}
              >
                <Bin />
                  {t('common.actions.cancel')}
              </Button>
              <div className={styles.confirm_button_block}>
                {!isMobile && (
                  <Button onClick={closeModal} className={styles.return_button}>
                    {t('common.actions.cancel')}
                  </Button>
                )}
                <Button className={styles.confirm_button} variant='contained' type={'submit'}>
                  {t('common.actions.confirm')}
                </Button>
              </div>
            </div>
          </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default PartialTask;
